/**
 * Free-crop cropper UX, shared by the Crud image input and the canvas
 * utils.image BlockPanel. Activates when `outputOptions.cropMode === 'free'`:
 * injects the aspect-preset / reset / lock controls bar under the cropper,
 * opens the box maxed to the image, and renders the result with `fillColor`
 * padding + a `maxAspect` cap.
 *
 * Host component must expose `this.$refs.avatarCropper` (the <avatar-cropper>)
 * and a `this.outputOptionsObject` computed. `cropUnlocked` is provided here.
 */
export default {
    data() {
        return {
            // Free-crop "lock": true = box restrained to the image (viewMode 1);
            // toggled off → viewMode 0 lets the box leave the image to add padding.
            cropUnlocked: false,
        };
    },

    computed: {
        // Free crop renders the photo at its cropped aspect (no fixed box), so
        // object-fit ("Hoe de foto tonen?") has no effect there.
        isFreeCrop() {
            return (this.outputOptionsObject?.cropMode) === 'free';
        },
    },

    methods: {
        // Cropper options for free mode — host's cropperOptions returns this when free.
        freeCropperOptions() {
            const o = this.outputOptionsObject || {};
            const base = {
                // Locked ("Binnen kader") = viewMode 1: the box stops at the image
                // edge. Unlocked = viewMode 0, so it can leave the image to add
                // padding. (viewMode is init-only → toggling rebuilds the cropper.)
                viewMode: this.cropUnlocked ? 0 : 1,
                zoomable: true,
                guides: true,   // rule-of-thirds grid as an alignment aid
                center: true,
                autoCropArea: o.autoCropArea ?? 1,
                aspectRatio: 16 / 9,        // common default; "Vrij" preset frees it
                dragMode: 'move',           // background drag pans the photo
                movable: true,
                cropBoxMovable: true,
                cropBoxResizable: true,
                ready: this.onCropperReady,
                // NB: don't map `minCropWidth` to Cropper's `minCropBoxWidth` — that's
                // in screen px, so a value like 600 locks the box. Enforce the output
                // floor at submit instead.
            };
            // Block-level cropper options still win over the resolved defaults.
            return { ...base, ...o };
        },

        // Cropper 'ready': inject the controls bar and open with the box maxed to
        // the whole image (largest box of the current aspect that fits).
        onCropperReady() {
            const cropper = this.$refs.avatarCropper?.cropper;
            if (!cropper) {
                return;
            }
            this.injectCropSidebar();

            try {
                const c = cropper.getCanvasData();
                cropper.setCropBoxData({ left: c.left, top: c.top, width: c.width, height: c.height });
            } catch (e) { /* best-effort */ }
        },

        // Build the controls bar below the image. FREE: clickable aspect presets
        // (16:9 active) + reset + Binnen-kader lock. FIXED: presets shown disabled
        // (matching ratio highlighted) with a "locked" badge.
        injectCropSidebar() {
            const container = this.$refs.avatarCropper?.$el?.querySelector('.avatar-cropper-container');
            const footer = container?.querySelector('.avatar-cropper-footer');
            if (!container || !footer || container.querySelector('.crop-controls')) {
                return;
            }

            const o = this.outputOptionsObject || {};
            const free = o.cropMode === 'free';
            const fixedAspect = (o.width && o.height)
                ? (o.width / o.height)
                : (Number(o.cropAspect) || null);
            // Free mode opens at the block's configured aspect (outputOptions.aspectRatio),
            // defaulting to 16:9 — highlight the preset that matches it.
            const freeInitialAspect = Number(o.aspectRatio) > 0 ? Number(o.aspectRatio) : 16 / 9;
            const near = (a, b) => Math.abs(a - b) < 0.02;

            const bar = document.createElement('div');
            bar.className = 'crop-controls';

            const presets = [
                { label: 'Vrij', value: NaN },
                { label: '16:9', value: 16 / 9 },
                { label: '4:3', value: 4 / 3 },
                { label: '1:1', value: 1 },
                { label: '3:4', value: 3 / 4 },
            ];
            presets.forEach((p) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                const active = free
                    ? (!isNaN(p.value) && near(p.value, freeInitialAspect))
                    : (fixedAspect && !isNaN(p.value) && near(p.value, fixedAspect));
                btn.className = 'crop-sidebar-btn crop-aspect-btn' + (active ? ' is-active' : '');
                btn.textContent = p.label;
                if (free) {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.setCropAspect(p.value, btn);
                    });
                } else {
                    btn.disabled = true;
                    btn.classList.add('is-disabled');
                    btn.title = 'De verhouding ligt vast voor dit blok';
                }
                bar.appendChild(btn);
            });

            // Reset (re-fit) — always available.
            const reset = document.createElement('button');
            reset.type = 'button';
            reset.className = 'crop-sidebar-btn crop-icon-btn crop-reset-btn';
            reset.innerHTML = '<i class="fas fa-arrows-rotate"></i>';
            reset.title = 'Passend — herstel uitsnede + zoom de hele afbeelding';
            reset.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.resetCrop();
            });
            bar.appendChild(reset);

            if (free) {
                const lock = document.createElement('button');
                lock.type = 'button';
                lock.className = 'crop-sidebar-btn crop-icon-btn crop-lock-btn';
                lock.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggleCropLock();
                });
                bar.appendChild(lock);
            } else {
                const badge = document.createElement('span');
                badge.className = 'crop-sidebar-btn crop-icon-btn crop-locked-badge';
                badge.innerHTML = '<i class="fas fa-lock"></i>';
                badge.title = 'De verhouding ligt vast voor dit blok — uploaden gebeurt in deze vaste verhouding';
                bar.appendChild(badge);
            }

            container.insertBefore(bar, footer); // below the image, above the footer
            this.updateCropLockButton();
        },

        // Reset the crop box + zoom back to the initial "whole image" fit.
        resetCrop() {
            const cropper = this.$refs.avatarCropper?.cropper;
            if (!cropper) {
                return;
            }
            cropper.reset();
        },

        setCropAspect(value, btn) {
            const cropper = this.$refs.avatarCropper?.cropper;
            if (!cropper) {
                return;
            }
            cropper.setAspectRatio(isNaN(value) ? NaN : value);
            // Max the box to the image for the chosen aspect.
            try {
                const c = cropper.getCanvasData();
                cropper.setCropBoxData({ left: c.left, top: c.top, width: c.width, height: c.height });
            } catch (e) { /* best-effort */ }
            this.$refs.avatarCropper?.$el
                ?.querySelectorAll('.crop-aspect-btn')
                .forEach((b) => b.classList.toggle('is-active', b === btn));
        },

        updateCropLockButton() {
            const btn = this.$refs.avatarCropper?.$el?.querySelector('.crop-lock-btn');
            if (btn) {
                btn.innerHTML = this.cropUnlocked
                    ? '<i class="fas fa-lock-open"></i>'
                    : '<i class="fas fa-lock"></i>';
                btn.title = this.cropUnlocked
                    ? 'Buiten kader — uitsnede mag buiten de afbeelding (marge)'
                    : 'Binnen kader — uitsnede blijft binnen de afbeelding';
                btn.classList.toggle('is-active', !this.cropUnlocked);
            }
        },

        // Toggle the restraint. viewMode is init-only, so rebuild the cropper
        // (after the recomputed cropperOptions prop propagates) to apply it.
        toggleCropLock() {
            this.cropUnlocked = !this.cropUnlocked;
            this.$nextTick(() => {
                const ac = this.$refs.avatarCropper;
                if (ac?.cropper) {
                    ac.cropper.destroy();
                    ac.createCropper();
                }
                this.updateCropLockButton();
            });
        },

        // Render the (possibly outside-the-image) crop box to a File: fills any
        // overflow with `fillColor` and pads to `maxAspect` if the crop is wider
        // than the cap. Returns the File for the host to upload as-is (no crop
        // coords → server stores it verbatim). `resp` is the Cropper instance.
        async renderFreeCropFile(resp, mimeType) {
            const oo = this.outputOptionsObject || {};
            const fillColor = oo.fillColor || '#ffffff';

            let canvas = resp.getCroppedCanvas({
                fillColor,
                maxWidth: 2400,
                maxHeight: 2400,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: 'high',
            });

            const maxAspect = Number(oo.maxAspect);
            if (maxAspect && canvas.height > 0 && canvas.width / canvas.height > maxAspect) {
                const padded = document.createElement('canvas');
                padded.width = canvas.width;
                padded.height = Math.round(canvas.width / maxAspect);
                const ctx = padded.getContext('2d');
                ctx.fillStyle = fillColor;
                ctx.fillRect(0, 0, padded.width, padded.height);
                ctx.drawImage(canvas, 0, Math.round((padded.height - canvas.height) / 2));
                canvas = padded;
            }

            const filename = this.$refs.avatarCropper.filename;
            const outType = (mimeType === 'image/png') ? 'image/png' : 'image/jpeg';

            return await new Promise((resolve) => {
                canvas.toBlob((blob) => {
                    resolve(blob ? new File([blob], filename, { type: blob.type || outType }) : null);
                }, outType, 0.92);
            });
        },
    },
};
